import React, { useState, useEffect } from 'react';
import api from '../services/api';
import SpaceBackground from '../components/layout/SpaceBackground';
import {
  LayoutList, Plus, Trash2, ChevronRight, Search,
  ToggleLeft, ToggleRight, CheckCircle, Clock, XCircle, Save
} from 'lucide-react';

const STEP_TYPES = [
  { value: 'review_info', label: 'Review Information' },
  { value: 'sign_engagement', label: 'Sign Engagement' },
  { value: 'upload_id', label: 'Upload Document' },
  { value: 'complete_questionnaire', label: 'Complete Questionnaire' },
  { value: 'make_payment', label: 'Make Payment' },
];

const AddStepFromTemplate = ({ templateId, progressId, existingStepIds, onAdd }) => {
  const [available, setAvailable] = useState([]);
  useEffect(() => {
    api.get(`/api/onboarding-admin/templates/${templateId}/steps`).then(({ data }) => {
      setAvailable(data.filter(s => !existingStepIds.includes(s.id)));
    }).catch(() => {});
  }, [templateId, existingStepIds]);

  const handleAdd = async (stepId) => {
    try {
      await api.post(`/api/onboarding-admin/progress/${progressId}/steps`, { template_step_id: stepId });
      onAdd();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  if (available.length === 0) return null;
  return (
    <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(148,163,184,0.2)' }}>
      <select onChange={e => { if (e.target.value) handleAdd(parseInt(e.target.value)); e.target.value = ''; }}
        style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid rgba(148,163,184,0.3)',
          background: 'rgba(15,23,42,0.8)', color: '#94A3B8', fontSize: 12 }}>
        <option value="">+ Add step from template...</option>
        {available.map(s => <option key={s.id} value={s.id}>{s.title} ({s.step_type})</option>)}
      </select>
    </div>
  );
};

const OnboardingAdmin = () => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('templates');

  // Template form
  const [showNewTemplate, setShowNewTemplate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPracticeArea, setNewPracticeArea] = useState('');

  // Step editing
  const [editingStep, setEditingStep] = useState(null);
  const [showAddStep, setShowAddStep] = useState(false);
  const [newStep, setNewStep] = useState({ step_type: 'review_info', title: '', description: '', is_required: true });

  // Client search
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientProgress, setClientProgress] = useState([]);

  const loadTemplates = async () => {
    try {
      const { data } = await api.get('/api/onboarding-admin/templates');
      setTemplates(data);
    } catch (err) {
      console.error('Failed to load templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadSteps = async (templateId) => {
    try {
      const { data } = await api.get(`/api/onboarding-admin/templates/${templateId}/steps`);
      setSteps(data);
    } catch (err) {
      console.error('Failed to load steps:', err);
    }
  };

  useEffect(() => { loadTemplates(); }, []);
  useEffect(() => { if (selectedTemplate) loadSteps(selectedTemplate.id); }, [selectedTemplate]);

  const createTemplate = async () => {
    if (!newName.trim()) return;
    try {
      const { data } = await api.post('/api/onboarding-admin/templates', {
        name: newName.trim(), practice_area: newPracticeArea.trim() || null
      });
      setNewName(''); setNewPracticeArea(''); setShowNewTemplate(false);
      loadTemplates();
      setSelectedTemplate(data);
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const toggleTemplate = async (tmpl) => {
    try {
      await api.patch(`/api/onboarding-admin/templates/${tmpl.id}`, { is_active: !tmpl.is_active });
      loadTemplates();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const deleteTemplate = async (tmpl) => {
    if (!window.confirm(`Delete "${tmpl.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/api/onboarding-admin/templates/${tmpl.id}`);
      if (selectedTemplate?.id === tmpl.id) { setSelectedTemplate(null); setSteps([]); }
      loadTemplates();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const addStep = async () => {
    if (!newStep.title.trim() || !selectedTemplate) return;
    try {
      await api.post(`/api/onboarding-admin/templates/${selectedTemplate.id}/steps`, newStep);
      setNewStep({ step_type: 'review_info', title: '', description: '', is_required: true });
      setShowAddStep(false);
      loadSteps(selectedTemplate.id);
      loadTemplates();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const saveStep = async (step) => {
    try {
      await api.patch(`/api/onboarding-admin/steps/${step.id}`, {
        step_type: step.step_type, title: step.title,
        description: step.description, is_required: step.is_required
      });
      setEditingStep(null);
      loadSteps(selectedTemplate.id);
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const deleteStep = async (stepId) => {
    if (!window.confirm('Remove this step?')) return;
    try {
      await api.delete(`/api/onboarding-admin/steps/${stepId}`);
      loadSteps(selectedTemplate.id);
      loadTemplates();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const moveStep = async (idx, direction) => {
    const newSteps = [...steps];
    const swapIdx = idx + direction;
    if (swapIdx < 0 || swapIdx >= newSteps.length) return;
    [newSteps[idx], newSteps[swapIdx]] = [newSteps[swapIdx], newSteps[idx]];
    setSteps(newSteps);
    try {
      await api.post(`/api/onboarding-admin/templates/${selectedTemplate.id}/reorder`, {
        step_ids: newSteps.map(s => s.id)
      });
    } catch (err) { alert('Reorder failed'); loadSteps(selectedTemplate.id); }
  };

  // Client search
  const searchClients = async () => {
    if (!clientSearch.trim()) return;
    try {
      const { data } = await api.get(`/api/contacts?search=${encodeURIComponent(clientSearch)}&limit=10`);
      setClientResults(data.contacts || data || []);
    } catch (err) { console.error(err); }
  };

  const loadClientProgress = async (contactId) => {
    try {
      const { data } = await api.get(`/api/onboarding-admin/clients/${contactId}/progress`);
      setClientProgress(data);
    } catch (err) { console.error(err); }
  };

  const removeClientStep = async (progressId, stepStatusId) => {
    if (!window.confirm('Remove this pending step?')) return;
    try {
      await api.delete(`/api/onboarding-admin/progress/${progressId}/steps/${stepStatusId}`);
      loadClientProgress(selectedClient.id);
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const stepStatusIcon = (status) => {
    if (status === 'completed') return <CheckCircle size={14} className="text-green-400" />;
    if (status === 'skipped') return <XCircle size={14} className="text-gray-400" />;
    return <Clock size={14} className="text-amber-400" />;
  };

  return (
    <SpaceBackground>
      <div style={{ padding: '2rem', maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h1 style={{ color: '#E2E8F0', display: 'flex', alignItems: 'center', gap: 10, margin: 0 }}>
            <LayoutList size={28} /> Onboarding Templates
          </h1>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setActiveTab('templates')}
              style={{ padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
                background: activeTab === 'templates' ? 'rgba(59,130,246,0.3)' : 'transparent',
                color: activeTab === 'templates' ? '#93C5FD' : '#94A3B8' }}>
              Templates
            </button>
            <button onClick={() => setActiveTab('clients')}
              style={{ padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
                background: activeTab === 'clients' ? 'rgba(59,130,246,0.3)' : 'transparent',
                color: activeTab === 'clients' ? '#93C5FD' : '#94A3B8' }}>
              Per-Client
            </button>
          </div>
        </div>

        {activeTab === 'templates' && (
          <div style={{ display: 'flex', gap: 20 }}>
            {/* Left: Template List */}
            <div style={{ width: 320, flexShrink: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ color: '#94A3B8', fontSize: 13, fontWeight: 600 }}>TEMPLATES</span>
                <button onClick={() => setShowNewTemplate(true)}
                  style={{ background: '#3B82F6', color: '#fff', border: 'none', borderRadius: 6,
                    padding: '4px 12px', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Plus size={14} /> New
                </button>
              </div>

              {showNewTemplate && (
                <div style={{ background: 'rgba(30,41,59,0.95)', borderRadius: 10, padding: 12, marginBottom: 8,
                  border: '1px solid rgba(148,163,184,0.2)' }}>
                  <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Template name..."
                    style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid rgba(148,163,184,0.3)',
                      background: 'rgba(15,23,42,0.8)', color: '#E2E8F0', fontSize: 13, marginBottom: 6, boxSizing: 'border-box' }} />
                  <input value={newPracticeArea} onChange={e => setNewPracticeArea(e.target.value)} placeholder="Practice area (optional)"
                    style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid rgba(148,163,184,0.3)',
                      background: 'rgba(15,23,42,0.8)', color: '#E2E8F0', fontSize: 13, marginBottom: 8, boxSizing: 'border-box' }} />
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={createTemplate} style={{ background: '#3B82F6', color: '#fff', border: 'none',
                      borderRadius: 6, padding: '4px 14px', cursor: 'pointer', fontSize: 13 }}>Create</button>
                    <button onClick={() => setShowNewTemplate(false)} style={{ background: 'transparent', color: '#94A3B8',
                      border: '1px solid rgba(148,163,184,0.3)', borderRadius: 6, padding: '4px 14px', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                  </div>
                </div>
              )}

              {templates.map(tmpl => (
                <div key={tmpl.id} onClick={() => setSelectedTemplate(tmpl)}
                  style={{ background: selectedTemplate?.id === tmpl.id ? 'rgba(59,130,246,0.15)' : 'rgba(30,41,59,0.95)',
                    borderRadius: 10, padding: 12, marginBottom: 6, cursor: 'pointer',
                    border: `1px solid ${selectedTemplate?.id === tmpl.id ? 'rgba(59,130,246,0.4)' : 'rgba(148,163,184,0.15)'}`,
                    opacity: tmpl.is_active ? 1 : 0.5 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ color: '#E2E8F0', fontSize: 14, fontWeight: 500 }}>{tmpl.name}</div>
                      <div style={{ color: '#64748B', fontSize: 12, marginTop: 2 }}>
                        {tmpl.step_count} steps · {tmpl.active_clients} active
                        {tmpl.practice_area && ` · ${tmpl.practice_area}`}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
                      <button onClick={() => toggleTemplate(tmpl)} title={tmpl.is_active ? 'Deactivate' : 'Activate'}
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 2,
                          color: tmpl.is_active ? '#22C55E' : '#EF4444' }}>
                        {tmpl.is_active ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                      </button>
                      <button onClick={() => deleteTemplate(tmpl)} title="Delete"
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 2, color: '#EF4444' }}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Right: Step Editor */}
            <div style={{ flex: 1 }}>
              {selectedTemplate ? (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <span style={{ color: '#94A3B8', fontSize: 13, fontWeight: 600 }}>
                      STEPS — {selectedTemplate.name}
                    </span>
                    <button onClick={() => setShowAddStep(true)}
                      style={{ background: '#3B82F6', color: '#fff', border: 'none', borderRadius: 6,
                        padding: '4px 12px', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Plus size={14} /> Add Step
                    </button>
                  </div>

                  {showAddStep && (
                    <div style={{ background: 'rgba(30,41,59,0.95)', borderRadius: 10, padding: 12, marginBottom: 10,
                      border: '1px solid rgba(148,163,184,0.2)' }}>
                      <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                        <select value={newStep.step_type} onChange={e => setNewStep(s => ({...s, step_type: e.target.value}))}
                          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid rgba(148,163,184,0.3)',
                            background: 'rgba(15,23,42,0.8)', color: '#E2E8F0', fontSize: 13 }}>
                          {STEP_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                        <input value={newStep.title} onChange={e => setNewStep(s => ({...s, title: e.target.value}))}
                          placeholder="Step title..." style={{ flex: 1, padding: '6px 10px', borderRadius: 6,
                            border: '1px solid rgba(148,163,184,0.3)', background: 'rgba(15,23,42,0.8)', color: '#E2E8F0', fontSize: 13 }} />
                      </div>
                      <input value={newStep.description} onChange={e => setNewStep(s => ({...s, description: e.target.value}))}
                        placeholder="Description (optional)" style={{ width: '100%', padding: '6px 10px', borderRadius: 6,
                          border: '1px solid rgba(148,163,184,0.3)', background: 'rgba(15,23,42,0.8)', color: '#E2E8F0',
                          fontSize: 13, marginBottom: 8, boxSizing: 'border-box' }} />
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <label style={{ color: '#94A3B8', fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <input type="checkbox" checked={newStep.is_required}
                            onChange={e => setNewStep(s => ({...s, is_required: e.target.checked}))} /> Required
                        </label>
                        <button onClick={addStep} style={{ background: '#3B82F6', color: '#fff', border: 'none',
                          borderRadius: 6, padding: '4px 14px', cursor: 'pointer', fontSize: 13 }}>Add</button>
                        <button onClick={() => setShowAddStep(false)} style={{ background: 'transparent', color: '#94A3B8',
                          border: '1px solid rgba(148,163,184,0.3)', borderRadius: 6, padding: '4px 14px', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                      </div>
                    </div>
                  )}

                  {steps.length === 0 ? (
                    <p style={{ color: '#64748B', textAlign: 'center', marginTop: '3rem' }}>No steps yet. Add one above.</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {steps.map((step, idx) => (
                        <div key={step.id} style={{ background: 'rgba(30,41,59,0.95)', borderRadius: 10, padding: 12,
                          border: '1px solid rgba(148,163,184,0.15)' }}>
                          {editingStep === step.id ? (
                            <div>
                              <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                                <select value={step.step_type} onChange={e => setSteps(ss => ss.map(s => s.id === step.id ? {...s, step_type: e.target.value} : s))}
                                  style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid rgba(148,163,184,0.3)',
                                    background: 'rgba(15,23,42,0.8)', color: '#E2E8F0', fontSize: 13 }}>
                                  {STEP_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                </select>
                                <input value={step.title} onChange={e => setSteps(ss => ss.map(s => s.id === step.id ? {...s, title: e.target.value} : s))}
                                  style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: '1px solid rgba(148,163,184,0.3)',
                                    background: 'rgba(15,23,42,0.8)', color: '#E2E8F0', fontSize: 13 }} />
                              </div>
                              <input value={step.description || ''} onChange={e => setSteps(ss => ss.map(s => s.id === step.id ? {...s, description: e.target.value} : s))}
                                placeholder="Description" style={{ width: '100%', padding: '6px 10px', borderRadius: 6,
                                  border: '1px solid rgba(148,163,184,0.3)', background: 'rgba(15,23,42,0.8)', color: '#E2E8F0',
                                  fontSize: 13, marginBottom: 6, boxSizing: 'border-box' }} />
                              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <label style={{ color: '#94A3B8', fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <input type="checkbox" checked={step.is_required}
                                    onChange={e => setSteps(ss => ss.map(s => s.id === step.id ? {...s, is_required: e.target.checked} : s))} /> Required
                                </label>
                                <button onClick={() => saveStep(step)} style={{ background: '#22C55E', color: '#fff', border: 'none',
                                  borderRadius: 6, padding: '4px 14px', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <Save size={12} /> Save
                                </button>
                                <button onClick={() => { setEditingStep(null); loadSteps(selectedTemplate.id); }}
                                  style={{ background: 'transparent', color: '#94A3B8', border: '1px solid rgba(148,163,184,0.3)',
                                    borderRadius: 6, padding: '4px 14px', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                              </div>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <button onClick={() => moveStep(idx, -1)} disabled={idx === 0}
                                  style={{ background: 'transparent', border: 'none', color: idx === 0 ? '#334155' : '#94A3B8',
                                    cursor: idx === 0 ? 'default' : 'pointer', padding: 0, fontSize: 10, lineHeight: 1 }}>&#9650;</button>
                                <button onClick={() => moveStep(idx, 1)} disabled={idx === steps.length - 1}
                                  style={{ background: 'transparent', border: 'none', color: idx === steps.length - 1 ? '#334155' : '#94A3B8',
                                    cursor: idx === steps.length - 1 ? 'default' : 'pointer', padding: 0, fontSize: 10, lineHeight: 1 }}>&#9660;</button>
                              </div>
                              <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => setEditingStep(step.id)}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <span style={{ color: '#64748B', fontSize: 12, minWidth: 20 }}>{idx + 1}.</span>
                                  <span style={{ color: '#E2E8F0', fontSize: 14 }}>{step.title}</span>
                                  <span style={{ color: '#64748B', fontSize: 11, background: 'rgba(100,116,139,0.2)',
                                    padding: '1px 6px', borderRadius: 4 }}>
                                    {STEP_TYPES.find(t => t.value === step.step_type)?.label || step.step_type}
                                  </span>
                                  {step.is_required && (
                                    <span style={{ color: '#F59E0B', fontSize: 11 }}>required</span>
                                  )}
                                </div>
                                {step.description && (
                                  <div style={{ color: '#64748B', fontSize: 12, marginTop: 2, marginLeft: 28 }}>{step.description}</div>
                                )}
                              </div>
                              <button onClick={() => deleteStep(step.id)} title="Remove step"
                                style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: '#EF4444' }}>
                                <Trash2 size={15} />
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div style={{ color: '#64748B', textAlign: 'center', marginTop: '4rem' }}>
                  <LayoutList size={48} style={{ opacity: 0.3, marginBottom: 12 }} />
                  <p>Select a template to edit its steps</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'clients' && (
          <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <input value={clientSearch} onChange={e => setClientSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && searchClients()}
                placeholder="Search by client name..."
                style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1px solid rgba(148,163,184,0.3)',
                  background: 'rgba(15,23,42,0.8)', color: '#E2E8F0', fontSize: 14 }} />
              <button onClick={searchClients} style={{ background: '#3B82F6', color: '#fff', border: 'none',
                borderRadius: 8, padding: '8px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Search size={16} /> Search
              </button>
            </div>

            {clientResults.length > 0 && !selectedClient && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 16 }}>
                {clientResults.map(c => (
                  <div key={c.id} onClick={() => { setSelectedClient(c); loadClientProgress(c.id); }}
                    style={{ background: 'rgba(30,41,59,0.95)', borderRadius: 8, padding: '10px 14px', cursor: 'pointer',
                      border: '1px solid rgba(148,163,184,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ color: '#E2E8F0', fontSize: 14 }}>{c.contact_name || c.name}</span>
                    <ChevronRight size={16} style={{ color: '#64748B' }} />
                  </div>
                ))}
              </div>
            )}

            {selectedClient && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                  <button onClick={() => { setSelectedClient(null); setClientProgress([]); }}
                    style={{ background: 'transparent', border: 'none', color: '#3B82F6', cursor: 'pointer', fontSize: 13 }}>
                    &larr; Back
                  </button>
                  <span style={{ color: '#E2E8F0', fontSize: 16, fontWeight: 500 }}>
                    {selectedClient.contact_name || selectedClient.name}
                  </span>
                </div>

                {clientProgress.length === 0 ? (
                  <p style={{ color: '#64748B', textAlign: 'center', marginTop: '2rem' }}>No onboarding records found.</p>
                ) : (
                  clientProgress.map(prog => (
                    <div key={prog.id} style={{ background: 'rgba(30,41,59,0.95)', borderRadius: 12, padding: 16,
                      marginBottom: 12, border: '1px solid rgba(148,163,184,0.2)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <div>
                          <span style={{ color: '#E2E8F0', fontSize: 14, fontWeight: 500 }}>{prog.template_name}</span>
                          {prog.case_number && <span style={{ color: '#64748B', fontSize: 12, marginLeft: 8 }}>Case: {prog.case_number}</span>}
                        </div>
                        <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 12,
                          background: prog.status === 'completed' ? 'rgba(34,197,94,0.2)' : 'rgba(245,158,11,0.2)',
                          color: prog.status === 'completed' ? '#22C55E' : '#F59E0B' }}>
                          {prog.status}
                        </span>
                      </div>

                      {prog.steps.map(step => (
                        <div key={step.step_status_id} style={{ display: 'flex', alignItems: 'center', gap: 8,
                          padding: '6px 0', borderTop: '1px solid rgba(148,163,184,0.1)' }}>
                          {stepStatusIcon(step.status)}
                          <span style={{ color: '#CBD5E1', fontSize: 13, flex: 1 }}>{step.title}</span>
                          <span style={{ color: '#64748B', fontSize: 11 }}>
                            {STEP_TYPES.find(t => t.value === step.step_type)?.label || step.step_type}
                          </span>
                          {step.is_required && <span style={{ color: '#F59E0B', fontSize: 10 }}>req</span>}
                          {step.status === 'pending' && prog.status === 'in_progress' && (
                            <button onClick={() => removeClientStep(prog.id, step.step_status_id)}
                              style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 2, color: '#EF4444' }}>
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>
                      ))}

                      {prog.status === 'in_progress' && prog.template_id && (
                        <AddStepFromTemplate
                          templateId={prog.template_id}
                          progressId={prog.id}
                          existingStepIds={prog.steps.map(s => s.template_step_id)}
                          onAdd={() => loadClientProgress(selectedClient.id)}
                        />
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </SpaceBackground>
  );
};

export default OnboardingAdmin;
